/**
 * Which third-party vendor deployment may receive customer data, per policy.
 * registry.ts holds contract facts, policy.ts evaluates them; servers resolve
 * policies and domain bridges derive entitlements. See docs/vendor_policy.md.
 */

// `sts` is speech-to-speech: participant audio re-voiced by the vendor.
export type Service = "tts" | "stt" | "sts" | "llm" | "file_conversion";

// Ordered from most to least restrictive. `customer_scoped` means training
// only models that serve that customer, never shared ones.
export type TrainingPolicy = "not_allowed" | "customer_scoped" | "allowed";

export type Vendor =
  | "anthropic"
  | "assemblyai"
  | "aws"
  | "cartesia"
  | "cerebras"
  | "deepgram"
  | "elevenlabs"
  | "google"
  | "microsoft"
  | "openai"
  | "cloudconvert";

export const VENDORS = [
  "anthropic",
  "assemblyai",
  "aws",
  "cartesia",
  "cerebras",
  "deepgram",
  "elevenlabs",
  "google",
  "microsoft",
  "openai",
  "cloudconvert",
] as const satisfies readonly Vendor[];

export const bannedProvidersFromAllowed = (
  allowedProviders: readonly Vendor[]
): Vendor[] => VENDORS.filter((vendor) => !allowedProviders.includes(vendor));

export const allowedProvidersFromBanned = (
  bannedProviders: readonly Vendor[]
): Vendor[] => VENDORS.filter((vendor) => !bannedProviders.includes(vendor));

export interface Deployment {
  vendor: Vendor;
  service: Service;
  // `null` means the terms apply to all models
  model: string | null;
  // TODO(soon): support deployment regions
  // regions: readonly Region[];
  // the most permissive training the deployment's contract allows
  trainingPolicy: TrainingPolicy;
  hasZdr: boolean;
  hasBaa: boolean;
}

export interface CompliancePolicy {
  // the most permissive training tolerated
  trainingPolicy: TrainingPolicy;
  requireZdr: boolean;
  requireBaa: boolean;
  bannedProviders: readonly Vendor[];
}

export type PolicyViolationReason =
  | "vendor_banned"
  | "training_not_allowed"
  | "zdr_required"
  | "baa_required";

export type PolicyDecision =
  | { isAllowed: true }
  | { isAllowed: false; reason: PolicyViolationReason };
